
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { InterviewService } from '@/services/interview/interviewService';
import { toast } from 'sonner';

export function useFeederPublishing() {
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function handlePublish(interviewId: string) {
    try {
      setPublishingId(interviewId);
      toast.loading('Publiserer intervju...', { id: 'publishing' });
      
      console.log('useFeederPublishing - Starting publish for interview:', interviewId);
      const result = await InterviewService.publishInterview(interviewId);
      
      if (result) {
        console.log('useFeederPublishing - Publish successful, invalidating queries');
        
        // Invalidate and refetch all queries that might be affected
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['interviews'] }),
          queryClient.invalidateQueries({ queryKey: ['publishedInterviews'] })
        ]);
        
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['feederInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['interviews'] }),
          queryClient.refetchQueries({ queryKey: ['publishedInterviews'] })
        ]);
        
        // Add a small delay to let database updates propagate
        setTimeout(async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
            queryClient.invalidateQueries({ queryKey: ['publishedInterviews'] })
          ]);
          await Promise.all([
            queryClient.refetchQueries({ queryKey: ['feederInterviews'] }),
            queryClient.refetchQueries({ queryKey: ['publishedInterviews'] })
          ]);
        }, 1000);
        
        // Show success message with regular anchor link to prevent React Router context errors
        toast.success(
          <div>
            <p>Intervju ble publisert</p>
            <div className="mt-2">
              <a 
                href="/publish-history" 
                className="text-sm bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded"
              >
                Se i publiseringshistorikk
              </a>
            </div>
          </div>,
          { 
            id: 'publishing',
            duration: 5000
          }
        );
      } else {
        throw new Error('Kunne ikke publisere intervju');
      }
    } catch (error) {
      console.error('Feil ved publisering av intervju:', error);
      toast.error(`Kunne ikke publisere intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'publishing' });
    } finally {
      setPublishingId(null);
    }
  }

  return {
    publishingId,
    handlePublish
  };
}
